import React, { useState } from 'react';
import { SearchInput } from '@/components';
import { Button, Popover, Space, Select, Tag } from 'antd';
import { IoFunnelOutline, IoCloseOutline } from 'react-icons/io5';
import { REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { CUSTOMER_TYPE_OPTIONS, CREDIT_TERMS_OPTIONS } from '@/features/onboarding/constants/onboarding';

interface CustomerFiltersProps {
  currentSearchText: string;
  currentRegion: number | null;
  currentCustomerType: number | null;
  currentCreditTerms: number | null;
  isRegionAll: boolean;
  userRegion?: number;
  onSearchChange: (searchText: string) => void;
  onRegionChange: (region: number | null) => void;
  onCustomerTypeChange: (customerType: number | null) => void;
  onCreditTermsChange: (creditTerms: number | null) => void;
  onApplyFilters: (filters: {
    region: number | null;
    customerType: number | null;
    creditTerms: number | null;
  }) => void;
  onClearAll: () => void;
}

export const CustomerFilters: React.FC<CustomerFiltersProps> = ({
  currentSearchText,
  currentRegion,
  currentCustomerType,
  currentCreditTerms,
  isRegionAll,
  userRegion,
  onSearchChange,
  onRegionChange,
  onCustomerTypeChange,
  onCreditTermsChange,
  onApplyFilters,
  onClearAll,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Draft state — only committed when "Apply Filters" is clicked
  const [draftRegion, setDraftRegion] = useState<number | null>(currentRegion);
  const [draftCustomerType, setDraftCustomerType] = useState<number | null>(currentCustomerType);
  const [draftCreditTerms, setDraftCreditTerms] = useState<number | null>(currentCreditTerms);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftRegion(currentRegion);
      setDraftCustomerType(currentCustomerType);
      setDraftCreditTerms(currentCreditTerms);
    }
    setFilterPopoverOpen(open);
  };

  const handleApplyFilters = () => {
    onApplyFilters({ region: draftRegion, customerType: draftCustomerType, creditTerms: draftCreditTerms });
    setFilterPopoverOpen(false);
  };

  const handleClearAll = () => {
    setDraftRegion(null);
    setDraftCustomerType(null);
    setDraftCreditTerms(null);
    onClearAll();
  };

  // Helper function to get filter label
  const getFilterLabel = (type: string, value: number | null) => {
    if (value === null) return null;
    switch (type) {
      case 'region':
        return REGION_FILTERS_OPTIONS.find(opt => opt.value === value)?.label;
      case 'customerType':
        return CUSTOMER_TYPE_OPTIONS.find(opt => opt.value === value)?.label;
      case 'creditTerms':
        return CREDIT_TERMS_OPTIONS.find(opt => opt.value === value)?.label;
      default:
        return null;
    }
  };

  // Count active applied filters
  const activeFilterCount = [
    isRegionAll && currentRegion !== null,
    currentCustomerType !== null,
    currentCreditTerms !== null,
  ].filter(Boolean).length;

  return (
    <div className="mb-4">
      <div className="flex gap-3 items-center flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[250px] max-w-[500px]">
          <SearchInput
            placeholder="Search by name, email, or phone..."
            onChange={(e) => onSearchChange(e)}
            allowClear
          />
        </div>

        {/* More Filters Button */}
        <div>
          <Popover
            content={
              <div style={{ width: 380 }}>
                <div
                  style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
                  className="pr-2"
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Region Filter */}
                    {isRegionAll && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Region
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select region"
                          value={draftRegion === null ? undefined : draftRegion}
                          onChange={(value) => {
                            setDraftRegion(value === undefined || value === null ? null : value as number);
                          }}
                          options={REGION_FILTERS_OPTIONS}
                          allowClear
                        />
                      </div>
                    )}

                    {/* Customer Type Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Type
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select customer type"
                        value={draftCustomerType === null ? undefined : draftCustomerType}
                        onChange={(value) => {
                          setDraftCustomerType(value === undefined || value === null ? null : value as number);
                        }}
                        options={CUSTOMER_TYPE_OPTIONS}
                        allowClear
                      />
                    </div>

                    {/* Credit Terms Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Credit Terms
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select credit terms"
                        value={draftCreditTerms === null ? undefined : draftCreditTerms}
                        onChange={(value) => {
                          setDraftCreditTerms(value === undefined || value === null ? null : value as number);
                        }}
                        options={CREDIT_TERMS_OPTIONS}
                        allowClear
                      />
                    </div>
                  </Space>
                </div>

                {/* Footer */}
                <div
                  className="pt-3 mt-3 flex flex-row justify-end"
                  style={{ borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}
                >
                  <Space style={{ width: '100%' }}>
                    {activeFilterCount > 0 && (
                      <Button block onClick={handleClearAll}>
                        Clear All
                      </Button>
                    )}
                    <Button type="primary" block onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                  </Space>
                </div>
              </div>
            }
            title={
              <div className="flex items-center justify-between">
                <span>Filter Options</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs text-gray-500">{activeFilterCount} active</span>
                )}
              </div>
            }
            trigger="click"
            open={filterPopoverOpen}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
          >
            <Button icon={<IoFunnelOutline />} className='h-[44px]'>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </Popover>
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {isRegionAll && currentRegion !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onRegionChange(null)}>
              Region: {getFilterLabel('region', currentRegion)}
            </Tag>
          )}
          {currentCustomerType !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onCustomerTypeChange(null)}>
              Type: {getFilterLabel('customerType', currentCustomerType)}
            </Tag>
          )}
          {currentCreditTerms !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onCreditTermsChange(null)}>
              Credit Terms: {getFilterLabel('creditTerms', currentCreditTerms)}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            className='text-primary-500'
            icon={<IoCloseOutline />}
            onClick={onClearAll}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomerFilters;
